<template>
  <div class="social-auth">
    <btn
      block
      kind="google"
      class="google-button"
      :loading="submitting"
      loading-text=""
      :disabled="submitting"
      @click="googleLogin"
    >
      <align small-gap>
        <img
          class="google-icon"
          alt="Google Sign In"
          src="@/assets/google-icon.svg"
          width="15"
        />

        <span>{{ isLogin ? 'Sign in with Google' : 'Sign up with Google' }}</span>
      </align>
    </btn>

    <btn
      block
      kind="apple"
      class="apple-button"
      :loading="submitting"
      loading-text=""
      :disabled="submitting"
      @click="appleLogin"
    >
      <align small-gap>
        <img
          class="apple-icon"
          alt="Apple Sign In"
          src="@/assets/apple-icon.svg"
          width="15"
        />

        <span>{{ isLogin ? 'Sign in with Apple' : 'Sign up with Apple' }}</span>
      </align>
    </btn>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';

import { getFirebaseErrorMessage, getRecaptchaErrorMessage } from '@cardladder/shared/utils/auth.util';

export default {
  props: {
    type: {
      type: String,
      default: 'login',
    },
  },

  data() {
    return {
      submitting: false,
    };
  },

  computed: {
    isLogin() {
      return this.type === 'login';
    },

    recaptchaAction() {
      return this.isLogin ? 'login' : 'signup';
    },
  },

  methods: {
    async verifyRecaptcha() {
      // eslint-disable-next-line no-undef
      if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
        throw new Error('grecaptcha is not available');
      }

      // eslint-disable-next-line no-undef
      const token = await grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action: this.recaptchaAction });
      await firebase.functions().httpsCallable('httpVerifyRecaptcha')({ token, action: this.recaptchaAction });
    },

    getSocialLoginErrorMessage(error, provider) {
      const code = (error && error.code) || '';

      if (code.startsWith('auth/')) {
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          return 'Sign-in was cancelled. Please try again.';
        }

        return getFirebaseErrorMessage(code);
      }

      if (
        code.startsWith('functions/')
        || /grecaptcha|recaptcha|verification failed|missing token/i.test((error && error.message) || '')
      ) {
        return getRecaptchaErrorMessage(error);
      }

      return `Error logging in with ${provider}. Please try again.`;
    },

    async googleLogin() {
      this.$toasted.clear();
      this.submitting = true;

      const provider = new firebase.auth.GoogleAuthProvider();

      try {
        await this.verifyRecaptcha();
        await firebase.auth().signInWithPopup(provider);

        await this.$store.dispatch('boot', firebase.auth().currentUser);

        this.$emit('complete');
      } catch (e) {
        console.error('social:google', e.code, e.message, e);
        this.$toasted.error(this.getSocialLoginErrorMessage(e, 'google'));
      }

      this.submitting = false;
    },

    async appleLogin() {
      this.$toasted.clear();
      this.submitting = true;

      const provider = new firebase.auth.OAuthProvider('apple.com');

      provider.addScope('email');

      try {
        await this.verifyRecaptcha();
        await firebase.auth().signInWithPopup(provider);

        await this.$store.dispatch('boot', firebase.auth().currentUser);

        this.$emit('complete');
      } catch (e) {
        console.error('social:apple', e.code, e.message, e);
        this.$toasted.error(this.getSocialLoginErrorMessage(e, 'apple'));
      }

      this.submitting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.social-auth {
  @media($large-up) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: $gp * .5;
  }
}

.apple-button,
.google-button {
  margin-bottom: $gp * .5;
}
</style>
